import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/dialogs/Modal';
import { filterDataApi } from '@/api/services/filterData';
import { alertsProfileApi } from '@/api/services/alertsProfile';
import {
  MiFIDClassification,
  TsComparisonOperator,
  TsReason,
  TsTradeSession,
} from '@/api/types/traderSurveillance';

const OPERATOR_LABEL: Record<TsComparisonOperator, string> = {
  [TsComparisonOperator.NotApplicable]: '',
  [TsComparisonOperator.GreaterThan]: '>',
  [TsComparisonOperator.SmallerThan]: '<',
  [TsComparisonOperator.EqualTo]: '=',
};
const SESSION_LABEL: Record<TsTradeSession, string> = {
  [TsTradeSession.NotApplicable]: 'N/A',
  [TsTradeSession.Sydney]: 'Sydney (9PM - 6AM GMT)',
  [TsTradeSession.Tokyo]: 'Tokyo (12AM - 9AM GMT)',
  [TsTradeSession.London]: 'London (7AM - 4PM GMT)',
  [TsTradeSession.NewYork]: 'New York (12PM - 9PM GMT)',
  [TsTradeSession.Custom]: 'Custom',
};
const REASON_LABEL: Record<number, string> = {
  [TsReason.NotApplicable]: 'N/A',
  [TsReason.Client]: 'Client',
  [TsReason.Expert]: 'Expert',
  [TsReason.Dealer]: 'Dealer',
  [TsReason.Signal]: 'Signal',
  [TsReason.Mobile]: 'Mobile',
  [TsReason.Web]: 'Web',
};
const MIFID_LABEL: Record<MiFIDClassification, string> = {
  [MiFIDClassification.Retail]: 'Retail',
  [MiFIDClassification.PerSeProfessional]: 'Per Se Professional',
  [MiFIDClassification.ProfessionalOnRequest]: 'Professional On Request',
  [MiFIDClassification.EligibleCounterparty]: 'Eligible Counterparty',
  [MiFIDClassification.Default]: 'Default',
};

// Mimics the legacy C# "#,##0.##" format (grouped, up to 2 decimals, trailing zeros trimmed).
function fmtAmount(v?: number | null): string | null {
  if (v == null) return null;
  return v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function op(operator?: TsComparisonOperator | null, amount?: number | null): string | null {
  if (operator == null || operator === TsComparisonOperator.NotApplicable) return null;
  return `${OPERATOR_LABEL[operator]} ${fmtAmount(amount)}`;
}

function fmtDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function fmtTime(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

interface ViewProfileModalProps {
  id: string | null;
  onClose: () => void;
}

export function ViewProfileModal({ id, onClose }: ViewProfileModalProps) {
  const query = useQuery({
    queryKey: ['alertsProfile', 'detail', id],
    queryFn: () => alertsProfileApi.getProfile(id as string),
    enabled: id !== null,
  });
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getConnectors, staleTime: 5 * 60_000 });

  const rows = useMemo<[string, string | null][]>(() => {
    const p = query.data;
    if (!p) return [];
    const connectorName =
      p.connector != null ? (connectors.data?.find((c) => String(c.value) === String(p.connector))?.label ?? String(p.connector)) : null;
    return [
      ['Profile Tag', p.profileTag],
      ['Type', p.typeName ?? null],
      ['Priority', p.priority ?? null],
      ['History Date', fmtDate(p.historyDate)],
      ['Status', p.enabled ? 'Enabled' : 'Disabled'],
      ['Reason', p.reason != null ? (REASON_LABEL[p.reason] ?? null) : null],
      ['Trading Session', p.tradeSession != null ? SESSION_LABEL[p.tradeSession] : null],
      ['Open Time', fmtTime(p.openTime)],
      ['Close Time', fmtTime(p.closeTime)],
      ['Trade Time', p.tradeTime != null ? String(p.tradeTime) : null],
      ['Trade Points', p.tradePoints != null ? String(p.tradePoints) : null],
      ['Volume', op(p.volumeOperator, p.volume)],
      ['Notional', op(p.notionalOperator, p.notional)],
      ['Equity', op(p.equityOperator, p.equity)],
      ['Margin Level %', op(p.marginLevelOperator, p.marginLevel)],
      ['Per-Trade P/L', p.profitThresholdsPerTrade ? fmtAmount(p.profitThresholdsAmountPerTrade) : null],
      ['Cumulative P/L', p.cumulativeProfitThresholds ? fmtAmount(p.cumulativeProfitThresholdsAmount) : null],
      ['Include Unrealized P/L', p.cumulativeProfitThresholds ? (p.cumulativeProfitThresholdsFloating ? 'Yes' : 'No') : null],
      ['Overnight Days', p.overnightDays != null ? String(p.overnightDays) : null],
      ['Group', p.group ?? null],
      ['Connector', connectorName],
      ['Account Currency', p.accountCurrency ?? null],
      ['MiFID Classification', p.mifidClassification != null ? MIFID_LABEL[p.mifidClassification] : null],
      ['Login', p.login != null ? String(p.login) : null],
      ['Unique Id', p.uniqueId ?? null],
      ['Instruments', p.marInstruments ? 'MAR Instruments' : (p.instrument ?? null)],
    ];
  }, [query.data, connectors.data]);

  return (
    <Modal
      show={id !== null}
      title="Alert Profile Details"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-light" onClick={onClose}>
          Close
        </button>
      }
    >
      {query.isLoading ? (
        <div className="text-center text-muted py-3">
          <i className="fa fa-spinner fa-spin me-1" />
          Loading…
        </div>
      ) : query.isError || !query.data ? (
        <div className="text-center text-danger py-3">Could not load the profile.</div>
      ) : (
        <table className="dataTable table table-striped mb-0 text-start">
          <tbody>
            {rows
              .filter(([, v]) => v != null && v !== '')
              .map(([label, v]) => (
                <tr key={label}>
                  <td className="fw-semibold col-5">{label}</td>
                  <td className="col-7">{v}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
